import { useCallback, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import { formatDate } from "@/lib/dayjs/format-date";
import { useWindowSize } from "@/lib/react-use/use-window-size";

import { Button } from "../button/button";
import { Calendar } from "../calendar/calendar";
import CalendarIcon from "../icon/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover";

import type { CalendarSingleProps } from "../calendar/types/calendar-single";
import type { MouseEventHandler } from "react";

type Props = Omit<CalendarSingleProps, "mode"> & { className?: string };

export function DatePicker({ selected, onSelect, hideYear = false, canSelectPastDate = false, className }: Props) {
	const [isOpen, setIsOpen] = useState(false);
	const handleOpenChange = useCallback((open: boolean) => {
		setIsOpen(open);
	}, []);

	const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.preventDefault();
			setIsOpen(!isOpen);
		},
		[isOpen],
	);

	const { isLaptop } = useWindowSize();

	const numberOfMonths = isLaptop ? 2 : 1;

	const formattedDate = (() => {
		if (selected) {
			if (hideYear) {
				return formatDate(selected, "MM月DD日");
			} else {
				return formatDate(selected, "YYYY年MM月DD日");
			}
		}

		return "";
	})();

	return (
		<Popover open={isOpen} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					onClick={handleClick}
					variant={"input-ish"}
					className={cn("w-[280px] justify-start text-left font-normal", className)}
				>
					<CalendarIcon className="mr-2 h-4 w-4 text-zinc-600" />
					<p className="text-xs text-zinc-600">{formattedDate ? formattedDate : "日付を選択してください"}</p>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="z-[calc(infinity)] w-auto px-6 py-6">
				<Calendar
					mode={"single"}
					selected={selected}
					onSelect={onSelect}
					hideYear={hideYear}
					canSelectPastDate={canSelectPastDate}
					numberOfMonths={numberOfMonths}
				/>
			</PopoverContent>
		</Popover>
	);
}
